import React from "react";
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

interface FormSubmitAreaProps {
  currentSection: number;
  totalSections: number;
  allowEditing: boolean;
  areAllPagesConfirmed: boolean;
  finalConfirmation: boolean;
  isSaving: boolean;
  setFinalConfirmation: (value: boolean) => void;
  onSubmit: () => void;
}

export const FormSubmitArea: React.FC<FormSubmitAreaProps> = ({
  currentSection,
  totalSections,
  allowEditing,
  areAllPagesConfirmed,
  finalConfirmation,
  isSaving,
  setFinalConfirmation,
  onSubmit,
}) => {
  const theme = useTheme();

  if (currentSection !== totalSections - 1) return null;
  if (!allowEditing) return null;

  const isReady = areAllPagesConfirmed && finalConfirmation;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 4,
        p: 3,
        borderRadius: 3,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={finalConfirmation}
              disabled={isSaving}
              onChange={(e) => setFinalConfirmation(e.target.checked)}
              sx={{
                color: finalConfirmation ? "success.main" : "primary.main",
                "&.Mui-checked": { color: "success.main" },
              }}
            />
          }
          label={
            <Typography variant="body2" fontWeight={500}>
              I confirm that I have reviewed all previous pages and the
              information provided is complete and accurate
            </Typography>
          }
        />
      </Box>

      {/* Submit button - disabled until all confirmations are checked */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={!isReady || isSaving}
          sx={{
            minWidth: 240,
            py: 1.75,
            fontWeight: 700,
            borderRadius: 2.5,
            textTransform: "none",
            fontSize: "1rem",
            boxShadow: isReady
              ? `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`
              : "none",
            background: isReady
              ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`
              : undefined,
            transition: "all 0.2s ease-in-out",
            "&:hover:not(:disabled)": {
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
              transform: "translateY(-2px)",
            },
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            "Finish Review"
          )}
        </Button>
      </Box>
    </Paper>
  );
};
